import { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MapPin, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AddressLookupProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

interface NominatimResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  address?: {
    house_number?: string;
    road?: string;
    suburb?: string;
    city?: string;
    state?: string;
    postcode?: string;
    country?: string;
  };
}

interface AddressSuggestion {
  description: string;
  placeId: string;
  lat?: string;
  lon?: string;
}

export function AddressLookup({
  value,
  onChange,
  placeholder = 'Enter address',
  disabled = false,
  className,
}: AddressLookupProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const debounceTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Sync internal state with external value
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Fetch address suggestions from OpenStreetMap Nominatim
  const fetchSuggestions = useCallback(async (searchValue: string) => {
    if (!searchValue || searchValue.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    setIsLoading(true);
    console.log('🔍 Searching for:', searchValue);

    try {
      const params = new URLSearchParams({
        q: searchValue,
        format: 'json',
        addressdetails: '1',
        limit: '5',
      });

      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?${params}`,
        {
          signal: abortControllerRef.current.signal,
          headers: {
            'Accept': 'application/json',
            'User-Agent': 'LocationManagementApp/1.0', // Nominatim requires a user agent
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: NominatimResult[] = await response.json();
      console.log('📍 Nominatim API response:', { count: data.length });

      setIsLoading(false);

      if (data && data.length > 0) {
        const mappedSuggestions = data.map((result) => ({
          description: result.display_name,
          placeId: result.place_id.toString(),
          lat: result.lat,
          lon: result.lon,
        }));
        setSuggestions(mappedSuggestions);
        setOpen(true);
        console.log('✅ Found predictions:', data.length);
      } else {
        setSuggestions([]);
        setOpen(true);
        console.log('ℹ️ No results found');
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('🚫 Request cancelled');
        return;
      }
      console.error('❌ Error fetching predictions:', error);
      setIsLoading(false);
      setSuggestions([]);
      setOpen(false);
    }
  }, []);

  // Handle input change with debouncing
  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
    onChange(newValue);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for debounced search (500ms for Nominatim - be nice to free service)
    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 500);
  };

  // Handle address selection
  const handleSelectAddress = (suggestion: AddressSuggestion) => {
    setInputValue(suggestion.description);
    onChange(suggestion.description);
    setOpen(false);
    setSuggestions([]);
  };

  // Cleanup debounce timer and abort controller
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative">
          <Input
            value={inputValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleInputChange(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            className={cn('pr-8', className)}
            onFocus={() => {
              if (suggestions.length > 0) {
                setOpen(true);
              }
            }}
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : (
              <MapPin className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)]"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command>
          <CommandList>
            <CommandEmpty>
              {isLoading ? 'Searching addresses...' : 'No addresses found'}
            </CommandEmpty>
            {suggestions.length > 0 && (
              <CommandGroup>
                {suggestions.map((suggestion) => (
                  <CommandItem
                    key={suggestion.placeId}
                    value={suggestion.description}
                    onSelect={() => handleSelectAddress(suggestion)}
                    className="cursor-pointer"
                  >
                    <MapPin className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{suggestion.description}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
